import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Activity, TrendingUp, Zap, ArrowLeft } from 'lucide-react' // MapPin, Building2, Globe, X - commented out for deployment
import { toast } from 'react-toastify'
// import { AddressAutofill } from '@mapbox/search-js-react' // Commented out for deployment
import { api } from '../lib/api'
import type { BusinessScope } from '../types/brand'
import PlanSelectionModal from './PlanSelectionModal'
// import {
//   BUSINESS_SCOPE_LABELS,
//   BUSINESS_SCOPE_DESCRIPTIONS,
//   isValidLocationFormat,
//   formatLocation
// } from '../types/brand' // Commented out for deployment

interface BrandFormProps {
  onSuccess: (brand: any) => void
  onCancel?: () => void
  submitButtonText?: string
  showCancelButton?: boolean
  existingIndustries?: string[]
  isFirstBrand?: boolean // Determines if subscription step is needed
}

// const SCOPE_OPTIONS: BusinessScope[] = ['LOCAL', 'REGIONAL', 'NATIONAL'] // Commented out for deployment

export default function BrandForm({
  onSuccess,
  onCancel,
  submitButtonText = 'Create Brand',
  showCancelButton = true,
  existingIndustries = [],
  isFirstBrand = true
}: BrandFormProps) {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<'details' | 'subscription'>('details')
  const [createdBrand, setCreatedBrand] = useState<any>(null)
  const [selectedPlan, setSelectedPlan] = useState<{ id: 'STARTER' | 'GROWTH' | 'PRO'; name: string; monthlyPrice: number } | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    industry: '',
    description: '',
    businessScope: 'NATIONAL' as BusinessScope,
    serviceAreas: [] as string[],
    primaryLocation: '' as string | undefined
  })
  const [loading, setLoading] = useState(false)
  const [generatingDescription, setGeneratingDescription] = useState(false)
  const [showIndustrySuggestions, setShowIndustrySuggestions] = useState(false)
  // const [currentLocationInput, setCurrentLocationInput] = useState('') // Commented out for deployment
  // const [locationError, setLocationError] = useState('') // Commented out for deployment
  const hasAttemptedFetch = useRef(false)

  const PLANS = [
    {
      id: 'STARTER' as const,
      name: 'Starter',
      description: '10 queries, 3 competitors, monthly analysis',
      monthlyPrice: 15,
      icon: Activity,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      id: 'GROWTH' as const,
      name: 'Growth',
      description: '30 queries, 6 competitors, AI insights',
      monthlyPrice: 29,
      icon: TrendingUp,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      recommended: true
    },
    {
      id: 'PRO' as const,
      name: 'Pro',
      description: '75 queries, 9 competitors, weekly analysis',
      monthlyPrice: 69,
      icon: Zap,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ]

  // const needsLocationInput = formData.businessScope === 'LOCAL' ||
  //                           formData.businessScope === 'REGIONAL' // Commented out for deployment

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    let value = e.target.value

    // Normalize website URL
    if (e.target.name === 'website') {
      // Remove https:// or http://
      value = value.replace(/^https?:\/\//, '')
      // Reset fetch flag when website changes
      hasAttemptedFetch.current = false
    }

    // Show industry suggestions when typing
    if (e.target.name === 'industry') {
      setShowIndustrySuggestions(value.length > 0)
    }

    setFormData({
      ...formData,
      [e.target.name]: value
    })
  }

  const handleIndustryBlur = async () => {
    // Wait a bit to allow clicking on suggestions
    setTimeout(async () => {
      setShowIndustrySuggestions(false)

      // Normalize industry name if one was entered
      if (formData.industry && formData.industry.trim()) {
        try {
          const response = await api.post('/brands/normalize-industry', {
            industry: formData.industry
          })

          if (response.data.normalized && response.data.normalized !== formData.industry) {
            setFormData(prev => ({
              ...prev,
              industry: response.data.normalized
            }))

            if (response.data.matched) {
              toast.info(`Matched to existing industry: ${response.data.normalized}`)
            }
          }
        } catch (error) {
          console.error('Failed to normalize industry:', error)
        }
      }
    }, 200)
  }

  const handleIndustrySelect = (industry: string) => {
    setFormData(prev => ({
      ...prev,
      industry
    }))
    setShowIndustrySuggestions(false)
  }

  // COMMENTED OUT FOR DEPLOYMENT
  // const handleBusinessScopeChange = (scope: BusinessScope) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     businessScope: scope,
  //     // Clear location data when switching to NATIONAL
  //     serviceAreas: scope === 'NATIONAL' ? [] : prev.serviceAreas,
  //     primaryLocation: scope === 'NATIONAL' ? '' : prev.primaryLocation
  //   }))
  //   setCurrentLocationInput('')
  //   setLocationError('')
  // }

  // COMMENTED OUT FOR DEPLOYMENT
  // const handleAddLocation = (locationString?: string) => {
  //   const trimmed = (locationString || currentLocationInput).trim()
  //
  //   if (!trimmed) {
  //     setLocationError('Please enter a location')
  //     return
  //   }
  //
  //   if (!isValidLocationFormat(trimmed)) {
  //     setLocationError('Please use format: City, State (e.g., San Diego, CA)')
  //     return
  //   }
  //
  //   const formatted = formatLocation(trimmed)
  //
  //   // Check for duplicates
  //   if (formData.serviceAreas.includes(formatted)) {
  //     setLocationError('This location is already added')
  //     return
  //   }
  //
  //   // Add to service areas
  //   setFormData(prev => ({
  //     ...prev,
  //     serviceAreas: [...prev.serviceAreas, formatted],
  //     // If first location, set as primary
  //     primaryLocation: !prev.primaryLocation ? formatted : prev.primaryLocation
  //   }))
  //
  //   // Clear input
  //   setCurrentLocationInput('')
  //   setLocationError('')
  // }

  // COMMENTED OUT FOR DEPLOYMENT
  // const handleMapboxRetrieve = (result: any) => {
  //   // Extract city and state/region from Mapbox result
  //   const context = result.features?.[0]?.context || []
  //   const properties = result.features?.[0]?.properties || {}
  //
  //   // Try to get place (city) and region (state)
  //   let city = properties.place || ''
  //   let region = properties.region || ''
  //
  //   // Fallback to context array if properties are empty
  //   if (!city || !region) {
  //     context.forEach((item: any) => {
  //       if (item.id?.startsWith('place.')) {
  //         city = item.text
  //       } else if (item.id?.startsWith('region.')) {
  //         region = item.short_code?.replace('US-', '') || item.text
  //       }
  //     })
  //   }
  //
  //   if (city && region) {
  //     const locationString = `${city}, ${region}`
  //     setCurrentLocationInput(locationString)
  //   }
  // }

  // COMMENTED OUT FOR DEPLOYMENT
  // const handleRemoveLocation = (location: string) => {
  //   setFormData(prev => {
  //     const newServiceAreas = prev.serviceAreas.filter(l => l !== location)
  //     return {
  //       ...prev,
  //       serviceAreas: newServiceAreas,
  //       // If removing primary location, set new primary
  //       primaryLocation: location === prev.primaryLocation
  //         ? (newServiceAreas[0] || '')
  //         : prev.primaryLocation
  //     }
  //   })
  // }

  // COMMENTED OUT FOR DEPLOYMENT
  // const handleSetPrimary = (location: string) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     primaryLocation: location
  //   }))
  // }

  // COMMENTED OUT FOR DEPLOYMENT
  // const handleLocationKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (e.key === 'Enter') {
  //     e.preventDefault()
  //     handleAddLocation()
  //   }
  // }

  const getFilteredIndustries = () => {
    if (!formData.industry) return existingIndustries
    const search = formData.industry.toLowerCase()
    return existingIndustries.filter(ind =>
      ind.toLowerCase().includes(search)
    )
  }

  const handleWebsiteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // Move focus to description field
      document.getElementById('description')?.focus()
    }
  }

  const handleWebsiteBlur = async () => {
    // Clean up website URL when user leaves the field
    if (formData.website) {
      const cleanedWebsite = formData.website.replace(/[/.]+$/, '')

      // Auto-generate brand info if website is valid, fields are empty, and we haven't already tried
      if (cleanedWebsite && !formData.companyName && !generatingDescription && !hasAttemptedFetch.current) {
        hasAttemptedFetch.current = true
        setGeneratingDescription(true)
        try {
          const fullUrl = `https://${cleanedWebsite}`
          const response = await api.post('/brands/generate-description', {
            website: fullUrl
          })

          // COMMENTED OUT FOR DEPLOYMENT - Location extraction logic
          // const locationData = response.data.location || response.data.city
          // let initialServiceAreas: string[] = []
          // let initialPrimaryLocation: string | undefined
          // let initialBusinessScope: BusinessScope = 'NATIONAL'
          //
          // // If we got location data, try to parse and pre-populate
          // if (locationData && typeof locationData === 'string') {
          //   const formatted = formatLocation(locationData)
          //   if (isValidLocationFormat(formatted)) {
          //     initialServiceAreas = [formatted]
          //     initialPrimaryLocation = formatted
          //     initialBusinessScope = 'LOCAL' // Default to LOCAL if we have a city
          //   }
          // }

          // Use functional update to ensure we have the latest state
          setFormData(prev => ({
            ...prev,
            website: cleanedWebsite,
            companyName: response.data.companyName || '',
            industry: response.data.industry || '',
            description: response.data.description || ''
            // businessScope defaults to 'NATIONAL' from initial state
            // serviceAreas defaults to [] from initial state
            // primaryLocation defaults to '' from initial state
          }))

          // COMMENTED OUT FOR DEPLOYMENT
          // // If we pre-populated a location, show a toast
          // if (initialServiceAreas.length > 0) {
          //   toast.info(`Detected location: ${initialServiceAreas[0]}`)
          // }
        } catch (error: any) {
          console.error('Failed to generate brand information:', error)
          const errorMessage = error.response?.data?.error || 'Failed to extract brand information. You can add it manually.'
          toast.error(errorMessage)
          // Still update the website field even if generation failed
          setFormData(prev => ({
            ...prev,
            website: cleanedWebsite
          }))
        } finally {
          setGeneratingDescription(false)
        }
      } else {
        // Just clean the website if fields are already filled or we've already tried
        setFormData(prev => ({
          ...prev,
          website: cleanedWebsite
        }))
      }
    }
  }

  const handlePlanSelect = (plan: typeof PLANS[0]) => {
    setSelectedPlan(plan)
    setShowPaymentModal(true)
  }

  const handlePaymentSuccess = () => {
    // Payment successful, call onSuccess with the brand
    setShowPaymentModal(false)
    if (createdBrand) {
      onSuccess(createdBrand)
    }
  }

  const handleBackToDetails = () => {
    setCurrentStep('details')
    setCreatedBrand(null)
    setSelectedPlan(null)
  }

  const validateWebsite = (website: string): { valid: boolean; message?: string } => {
    if (!website || website.trim() === '') {
      return { valid: false, message: 'Website is required' }
    }

    // Clean the website
    const cleaned = website.trim().replace(/^https?:\/\//, '').replace(/[/.]+$/, '')

    // Check if it has at least one dot (domain.tld)
    if (!cleaned.includes('.')) {
      return { valid: false, message: 'Please enter a valid domain (e.g., example.com)' }
    }

    // Check if TLD exists and is at least 2 characters
    const parts = cleaned.split('.')
    const tld = parts[parts.length - 1]

    if (!tld || tld.length < 2) {
      return { valid: false, message: 'Please enter a valid domain with a proper extension (e.g., .com, .org)' }
    }

    // Check if domain part exists
    const domain = parts[parts.length - 2]
    if (!domain || domain.length < 1) {
      return { valid: false, message: 'Please enter a valid domain name' }
    }

    // Check for invalid characters
    if (!/^[a-zA-Z0-9.-]+$/.test(cleaned)) {
      return { valid: false, message: 'Domain contains invalid characters' }
    }

    return { valid: true }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.companyName.trim() || !formData.website.trim()) {
      toast.error('Company name and website are required')
      return
    }

    // Validate website format
    const validation = validateWebsite(formData.website)
    if (!validation.valid) {
      toast.error(validation.message || 'Invalid website URL')
      return
    }

    // COMMENTED OUT FOR DEPLOYMENT - Location validation
    // Validate location input for local scopes
    // if (needsLocationInput && formData.serviceAreas.length === 0) {
    //   toast.error('Please add at least one location for your service area')
    //   return
    // }

    setLoading(true)

    try {
      // Clean and add https:// prefix to website before submitting
      const cleanedWebsite = formData.website.trim().replace(/^https?:\/\//, '').replace(/[/.]+$/, '')
      const response = await api.post('/brands', {
        companyName: formData.companyName.trim(),
        website: `https://${cleanedWebsite}`,
        industry: formData.industry.trim() || undefined,
        description: formData.description.trim() || undefined,
        businessScope: formData.businessScope,
        serviceAreas: formData.serviceAreas,
        primaryLocation: formData.primaryLocation || undefined
      })

      // If first brand, we're done - brand has TRIAL status
      if (isFirstBrand) {
        onSuccess(response.data)
      } else {
        // Not first brand - need subscription, move to step 2
        setCreatedBrand(response.data)
        setCurrentStep('subscription')
      }
    } catch (error: any) {
      console.error('Failed to create brand:', error)

      // Check if brand limit was reached - redirect to subscription page
      if (error.response?.data?.error === 'BRAND_LIMIT_REACHED') {
        navigate('/settings/subscription?reason=brand_limit')
        return
      }

      // Other errors
      const errorMessage = error.response?.data?.message || 'Failed to add brand'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Render subscription selection step
  if (currentStep === 'subscription' && createdBrand) {
    return (
      <div className="space-y-6">
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBackToDetails}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Brand Details
          </button>
          <div className="text-sm text-gray-500">
            Step 2 of 2: Select Subscription
          </div>
        </div>

        {/* Brand created confirmation */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Brand created: {createdBrand.companyName}
              </h3>
              <p className="mt-1 text-sm text-green-700">
                Select a subscription plan to activate and start monitoring
              </p>
            </div>
          </div>
        </div>

        {/* Plan selection */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Your Plan</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PLANS.map((plan) => {
              const Icon = plan.icon
              return (
                <button
                  key={plan.id}
                  onClick={() => handlePlanSelect(plan)}
                  type="button"
                  className="relative flex flex-col p-6 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:shadow-lg transition-all text-left group"
                >
                  <div className={`p-3 rounded-lg ${plan.bgColor} inline-flex self-start mb-4`}>
                    <Icon className={`h-6 w-6 ${plan.color}`} />
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 flex-grow">{plan.description}</p>

                  <div className="flex items-baseline justify-between border-t border-gray-200 pt-4">
                    <div>
                      <span className="text-3xl font-bold text-gray-900">${plan.monthlyPrice}</span>
                      <span className="text-gray-600 ml-1">/month</span>
                    </div>
                    <span className="text-indigo-600 font-medium group-hover:underline">
                      Select →
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> You'll be able to choose monthly or annual billing on the next step.
            Annual billing saves 10%.
          </p>
        </div>

        {/* Payment Modal */}
        {selectedPlan && (
          <PlanSelectionModal
            isOpen={showPaymentModal}
            onClose={() => {
              setShowPaymentModal(false)
              setSelectedPlan(null)
            }}
            plan={selectedPlan}
            userBrandId={createdBrand.userBrandId}
            isNewCustomer={false}
            onSuccess={handlePaymentSuccess}
          />
        )}
      </div>
    )
  }

  // Render brand details form (step 1)
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Step indicator for multi-step flow */}
      {!isFirstBrand && (
        <div className="text-sm text-gray-500 text-right mb-4">
          Step 1 of 2: Brand Details
        </div>
      )}
      <div>
        <label htmlFor="website" className="block text-sm font-medium text-gray-700">
          Website <span className="text-red-500">*</span>
        </label>
        <div className={`mt-1 flex rounded-md shadow-sm transition-all ${!formData.companyName ? 'ring-2 ring-indigo-500 ring-offset-2' : ''}`}>
          <span className={`inline-flex items-center px-3 rounded-l-md border border-r-0 ${!formData.companyName ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50'} text-gray-500 text-sm`}>
            https://
          </span>
          <input
            id="website"
            name="website"
            type="text"
            required
            value={formData.website}
            onChange={handleChange}
            onBlur={handleWebsiteBlur}
            onKeyDown={handleWebsiteKeyDown}
            placeholder="example.com"
            disabled={generatingDescription}
            className={`flex-1 block w-full px-3 py-2 border ${!formData.companyName ? 'border-indigo-500' : 'border-gray-300'} rounded-none rounded-r-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed`}
            autoFocus
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {!formData.companyName ? '👆 Start here - enter your website to auto-populate all fields' : 'Brand information extracted successfully'}
        </p>
      </div>

      {generatingDescription ? (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-200 p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
            <p className="text-md font-medium text-gray-700">
              Extracting brand information from website...
            </p>
          </div>
        </div>
      ) : !formData.companyName ? (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-dashed border-gray-300 p-8">
          <div className="text-center">
            <Sparkles className="mx-auto h-6 w-6 text-purple-400 mb-3" />
            <h3 className="text-md font-medium text-gray-700 mb-2">
              We'll auto generate your brand details from your domain.
            </h3>
            <p className="text-sm text-gray-500">
              You'll be able to modify before saving if needed.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              id="companyName"
              name="companyName"
              type="text"
              required
              value={formData.companyName}
              onChange={handleChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Auto-populated from website"
            />
          </div>

          {/* Business Scope Section - TEMPORARILY COMMENTED OUT FOR DEPLOYMENT */}
          {/* TODO: Re-enable after deployment */}
          {/* <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Business Scope
            </label>
            <div className="space-y-2">
              {SCOPE_OPTIONS.map(scope => (
                <div
                  key={scope}
                  onClick={() => handleBusinessScopeChange(scope)}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                    formData.businessScope === scope
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className="flex-shrink-0">
                      {scope === 'LOCAL' && <MapPin className="h-4 w-4 text-indigo-600" />}
                      {scope === 'REGIONAL' && <Building2 className="h-4 w-4 text-indigo-600" />}
                      {scope === 'NATIONAL' && <Globe className="h-4 w-4 text-indigo-600" />}
                    </div>
                    <div className="flex-1 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {BUSINESS_SCOPE_LABELS[scope]}
                        </p>
                        <p className="text-xs text-gray-600">
                          {BUSINESS_SCOPE_DESCRIPTIONS[scope]}
                        </p>
                      </div>
                      {formData.businessScope === scope && (
                        <div className="w-4 h-4 rounded-full bg-indigo-600 flex items-center justify-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {needsLocationInput && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Areas
                  <span className="text-gray-500 text-xs ml-2">
                    (Add cities/regions you serve)
                  </span>
                </label>

                <AddressAutofill
                  accessToken={import.meta.env.VITE_MAPBOX_API_KEY}
                  onRetrieve={handleMapboxRetrieve}
                  options={{
                    language: 'en',
                    country: 'US'
                  }}
                >
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={currentLocationInput}
                      onChange={e => {
                        setCurrentLocationInput(e.target.value)
                        setLocationError('')
                      }}
                      onKeyPress={handleLocationKeyPress}
                      placeholder="e.g., San Diego, CA"
                      autoComplete="address-level2"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                    />
                    <button
                      onClick={() => handleAddLocation()}
                      type="button"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium"
                    >
                      Add
                    </button>
                  </div>
                </AddressAutofill>

                {locationError && (
                  <p className="text-xs text-red-600 mb-2">{locationError}</p>
                )}

                <p className="text-xs text-gray-500">
                  Use format: <span className="font-mono">City, State</span>
                </p>

                {formData.serviceAreas.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {formData.serviceAreas.map(location => (
                      <div
                        key={location}
                        className="flex items-center justify-between px-3 py-2 bg-white border border-gray-200 rounded-md"
                      >
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-sm text-gray-900">{location}</span>
                          {location === formData.primaryLocation && (
                            <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {location !== formData.primaryLocation && (
                            <button
                              onClick={() => handleSetPrimary(location)}
                              type="button"
                              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                              Set Primary
                            </button>
                          )}
                          <button
                            onClick={() => handleRemoveLocation(location)}
                            type="button"
                            className="text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div> */}

          <div className="relative">
            <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
              Industry
            </label>
            <input
              id="industry"
              name="industry"
              type="text"
              value={formData.industry}
              onChange={handleChange}
              onFocus={() => setShowIndustrySuggestions(true)}
              onBlur={handleIndustryBlur}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="e.g., Technology, Healthcare, Finance"
              autoComplete="off"
            />
            {showIndustrySuggestions && getFilteredIndustries().length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {getFilteredIndustries().map((industry) => (
                  <button
                    key={industry}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleIndustrySelect(industry)
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 hover:text-indigo-700 text-sm"
                  >
                    {industry}
                  </button>
                ))}
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {existingIndustries.length > 0
                ? 'Start typing or select from your existing industries'
                : 'Enter your industry (e.g., Technology, Healthcare, Finance)'}
            </p>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Company Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={4}
              value={formData.description}
              onChange={handleChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Auto-populated from website"
            />
          </div>
        </>
      )}

      <div className="flex justify-end space-x-3">
        {showCancelButton && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={loading}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loading || generatingDescription}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating Brand...' : generatingDescription ? 'Analyzing website...' : (isFirstBrand ? submitButtonText : 'Next: Select Plan')}
        </button>
      </div>
    </form>
  )
}
